import { useEffect, useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { Calendar, Video, Users, UserPlus, LogOut, LayoutDashboard, GraduationCap, Heart, Home as HomeIcon } from "lucide-react";
import { Button } from "../components/ui/button";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, exact: true },
  { to: "/admin/events", label: "Events", icon: Calendar },
  { to: "/admin/sermons", label: "Sermons", icon: Video },
  { to: "/admin/memberships", label: "Memberships", icon: Users },
  { to: "/admin/join-team", label: "Team Applications", icon: UserPlus },
  { to: "/admin/bible-school", label: "Bible School", icon: GraduationCap },
  { to: "/admin/counselling", label: "Counselling", icon: Heart },
  { to: "/admin/homecell", label: "Homecell", icon: HomeIcon },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const doLogout = () => {
    logout();
    nav("/admin/login");
  };

  return (
    <div className="min-h-screen bg-white flex" data-testid="admin-layout">
      {/* sidebar */}
      <aside className="hidden md:flex w-64 bg-bone border-r border-border flex-col">
        <Link to="/" className="px-6 py-5 flex items-center gap-3 border-b border-border">
          <div className="w-9 h-9 bg-olive text-bone flex items-center justify-center font-heading font-black text-sm">ETI</div>
          <div className="leading-tight">
            <p className="font-heading font-bold text-ink text-sm">ETI Admin</p>
            <p className="text-xs text-muted_ink">Occupy and Build</p>
          </div>
        </Link>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => {
            const active = n.exact ? loc.pathname === n.to : loc.pathname.startsWith(n.to);
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`admin-nav-${n.label.toLowerCase().replace(" ", "-")}`}
                className={`flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
                  active ? "bg-olive text-bone" : "text-ink/70 hover:bg-cream"
                }`}
              >
                <n.icon size={16} /> {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-border">
          <p className="text-xs text-muted_ink truncate mb-2">{user?.email}</p>
          <Button variant="ghost" onClick={doLogout} data-testid="admin-logout-btn" className="w-full justify-start rounded-none text-ink hover:bg-terracotta hover:text-white">
            <LogOut size={14} className="mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* main */}
      <div className="flex-1 min-w-0">
        <div className="md:hidden border-b border-border bg-bone px-4 py-3 flex items-center justify-between">
          <p className="font-heading font-bold text-ink">ETI Admin</p>
          <Button variant="ghost" size="sm" onClick={doLogout} className="rounded-none">
            <LogOut size={14} className="mr-1" /> Sign out
          </Button>
        </div>
        <div className="md:hidden border-b border-border bg-white overflow-x-auto">
          <div className="flex">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} className={`px-4 py-3 text-xs font-semibold whitespace-nowrap border-r border-border ${loc.pathname === n.to ? "bg-olive text-bone" : "text-ink/70"}`}>
                {n.label}
              </Link>
            ))}
          </div>
        </div>
        <main className="p-6 md:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function AdminOverview() {
  const [stats, setStats] = useState({ events: 0, sermons: 0, memberships: 0, team: 0, ebi: 0, couns: 0, hc: 0 });

  useEffect(() => {
    Promise.all([
      api.get("/events"),
      api.get("/sermons"),
      api.get("/membership"),
      api.get("/join-team"),
      api.get("/bible-school/register"),
      api.get("/counselling"),
      api.get("/homecell"),
    ]).then(([e, s, m, t, b, c, h]) =>
      setStats({
        events: e.data.length, sermons: s.data.length, memberships: m.data.length,
        team: t.data.length, ebi: b.data.length, couns: c.data.length, hc: h.data.length,
      })
    );
  }, []);

  const cards = [
    { label: "Events", value: stats.events, to: "/admin/events", icon: Calendar },
    { label: "Sermons", value: stats.sermons, to: "/admin/sermons", icon: Video },
    { label: "Memberships", value: stats.memberships, to: "/admin/memberships", icon: Users },
    { label: "Team Applications", value: stats.team, to: "/admin/join-team", icon: UserPlus },
    { label: "Bible School", value: stats.ebi, to: "/admin/bible-school", icon: GraduationCap },
    { label: "Counselling", value: stats.couns, to: "/admin/counselling", icon: Heart },
    { label: "Homecell", value: stats.hc, to: "/admin/homecell", icon: HomeIcon },
  ];

  return (
    <div data-testid="admin-overview">
      <p className="overline text-terracotta mb-2">Dashboard</p>
      <h1 className="font-heading font-black text-4xl tracking-tighter text-olive">Welcome back.</h1>
      <p className="text-muted_ink mt-2">Manage content and view submissions.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
        {cards.map((c) => (
          <Link key={c.to} to={c.to} className="bg-white border border-border p-6 hover:border-olive hover:-translate-y-1 transition-all" data-testid={`stat-${c.label.toLowerCase().replace(" ", "-")}`}>
            <div className="flex items-center justify-between">
              <c.icon size={20} className="text-olive" />
              <span className="overline text-terracotta">View</span>
            </div>
            <p className="mt-8 font-heading font-black text-5xl tracking-tighter text-ink">{c.value}</p>
            <p className="text-sm text-muted_ink mt-2">{c.label}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
